import { faTrashCan } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState } from 'react';

import MainButton from '../../../components/agv-button';
import InputText from '../../../components/agv-forms-input';
import GenericModal from '../../../components/agv-generic-modal';
import { MODAL_SIZE, MODAL_SIZE_STYLE } from '../../../enums/modal-size.enum';
import { StatusDot } from '../../../redesign-v4/components/data-grid';
import isEmailValid from '../../../utils/is-email-valid.util';
import isPhoneNumberValid from '../../../utils/is-phone-number-valid.util';
import { formatDateTime } from '../client-diff';
import { Banner, CopyField, FormSection, Panel } from '../clients-ui';

const EMPTY_USER = {
  first_name: '',
  last_name: '',
  email: '',
  phone_number: '',
};

const STATUS = {
  ACTIVE: { label: 'Actif', color: '#00b75f' },
  PENDING: { label: 'Mot de passe à définir', color: '#b7791f' },
  STAGED: { label: 'Créé avec le client', color: '#76808c' },
};

/**
 * Lien d'inscription d'un administrateur qui n'a pas encore défini son mot
 * de passe : à lui transmettre quand l'invitation n'est pas partie par email.
 */
const InvitationBlock = ({ invitation, isEmailSendingEnabled }) => (
  <div className="tw-flex tw-flex-col tw-gap-3 tw-rounded-[6px] tw-border tw-border-solid tw-border-amber-200 tw-bg-amber-50/40 tw-p-3">
    <p className="tw-m-0 tw-text-[12px] tw-text-[#4a5360]">
      {isEmailSendingEnabled
        ? 'Invitation envoyée par email. Si elle n’est pas arrivée, transmettez ce lien à l’administrateur pour qu’il définisse son mot de passe.'
        : 'Aucun email envoyé (envois désactivés pour ce client) : transmettez ce lien à l’administrateur pour qu’il définisse son mot de passe.'}{' '}
      <span className="tw-font-medium">
        Sans expiration : valable jusqu’à ce que le mot de passe soit défini ou
        l’administrateur supprimé.
      </span>
    </p>
    <CopyField label="Lien d’inscription" value={invitation.url} />
    <CopyField label="Jeton" value={invitation.token} />
  </div>
);

const AdminUserRow = ({
  adminUser,
  isStaged,
  isEmailSendingEnabled,
  onRemove,
}) => {
  const status = isStaged
    ? STATUS.STAGED
    : adminUser.is_password_pending
      ? STATUS.PENDING
      : STATUS.ACTIVE;

  return (
    <div className="tw-flex tw-flex-col tw-gap-3 tw-border-0 tw-border-b tw-border-solid tw-border-[#eeeff2] tw-px-4 tw-py-3 last:tw-border-b-0">
      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
        <span className="tw-flex tw-size-8 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-main tw-text-[11px] tw-font-semibold tw-text-white">
          {`${adminUser.first_name?.[0] || ''}${adminUser.last_name?.[0] || ''}`.toUpperCase()}
        </span>
        <span className="tw-flex tw-min-w-[180px] tw-flex-1 tw-flex-col">
          <span className="tw-text-[13px] tw-font-medium tw-text-black">
            {adminUser.first_name} {adminUser.last_name}
          </span>
          <span className="tw-text-[12px] tw-text-[#76808c]">
            {[adminUser.email, adminUser.phone_number]
              .filter(Boolean)
              .join(' · ')}
          </span>
        </span>
        <span className="tw-flex tw-w-[220px] tw-flex-col tw-whitespace-nowrap tw-text-[12px]">
          <StatusDot color={status.color} label={status.label} />
          {!isStaged && adminUser.last_login_date && (
            <span className="tw-text-[11px] tw-text-[#76808c]">
              Dernière connexion {formatDateTime(adminUser.last_login_date)}
            </span>
          )}
        </span>
        <button
          type="button"
          onClick={() => onRemove(adminUser)}
          title={isStaged ? 'Retirer' : 'Supprimer l’administrateur'}
          className="tw-flex tw-size-8 tw-items-center tw-justify-center tw-rounded-[5px] tw-border tw-border-solid tw-border-transparent tw-bg-transparent tw-text-[#76808c] hover:tw-border-red-200 hover:tw-bg-red-50 hover:tw-text-red-600"
        >
          <FontAwesomeIcon icon={faTrashCan} className="tw-size-3.5" />
        </button>
      </div>
      {!isStaged && adminUser.invitation && (
        <InvitationBlock
          invitation={adminUser.invitation}
          isEmailSendingEnabled={isEmailSendingEnabled}
        />
      )}
    </div>
  );
};

/**
 * Administrateurs (rôle ADMIN) du client.
 *
 * - Création (`isStaged`) : la liste est locale, les administrateurs sont
 *   créés avec le client à la fin de l'assistant.
 * - Client existant : création et suppression immédiates (suppression après
 *   confirmation), tracées dans l'historique.
 */
export default function AdminUserStep({
  adminUsers,
  onAdd,
  onRemove,
  isAdding,
  isRemoving,
  isEmailSendingEnabled,
  isStaged = false,
}) {
  const [user, setUser] = useState(EMPTY_USER);
  const [userToRemove, setUserToRemove] = useState(null);
  // Les erreurs « champ vide » n'apparaissent qu'une fois la saisie commencée.
  const isTouched = Object.values(user).some(Boolean);

  const update = (key) => (value) =>
    setUser((previous) => ({ ...previous, [key]: value }));
  const emptyError = (label) =>
    isTouched ? `Le champ "${label}" ne peut être vide.` : undefined;

  const isPhoneInvalid =
    user.phone_number.length > 0 && !isPhoneNumberValid(user.phone_number);
  const isEmailAlreadyListed = adminUsers.some(
    (adminUser) =>
      adminUser.email?.trim().toLowerCase() === user.email.trim().toLowerCase()
  );

  const isDisabled =
    !user.first_name.trim() ||
    !user.last_name.trim() ||
    !isEmailValid(user.email) ||
    isEmailAlreadyListed ||
    isPhoneInvalid;

  const handleAdd = async () => {
    const isAdded = await onAdd({
      ...user,
      phone_number: user.phone_number || undefined,
    });

    if (isAdded) setUser(EMPTY_USER);
  };

  const handleRemove = (adminUser) => {
    if (isStaged) {
      onRemove(adminUser);
      return;
    }
    setUserToRemove(adminUser);
  };

  const confirmRemove = async () => {
    const isRemoved = await onRemove(userToRemove);
    if (isRemoved) setUserToRemove(null);
  };

  const addText = isStaged
    ? 'Ajouter'
    : isEmailSendingEnabled
      ? 'Créer et envoyer l’invitation'
      : 'Créer (lien d’inscription à transmettre)';

  return (
    <Panel>
      <FormSection
        title="Administrateurs"
        description="Rôle ADMIN : ils gèrent ensuite les utilisateurs, véhicules, entrepôts et paramètres de leur client. Ils n’ont pas accès aux outils internes."
      >
        {adminUsers.length === 0 ? (
          <Banner tone="warn">
            Aucun administrateur : personne ne pourra se connecter au
            back-office du client.
          </Banner>
        ) : (
          <div className="tw-overflow-hidden tw-rounded-[6px] tw-border tw-border-solid tw-border-[#e6e8ec]">
            {adminUsers.map((adminUser) => (
              <AdminUserRow
                key={adminUser.id ?? adminUser.email}
                adminUser={adminUser}
                isStaged={isStaged}
                isEmailSendingEnabled={isEmailSendingEnabled}
                onRemove={handleRemove}
              />
            ))}
          </div>
        )}
      </FormSection>

      <FormSection
        title="Ajouter un administrateur"
        description={
          isEmailSendingEnabled
            ? 'Il reçoit un email d’invitation pour définir son mot de passe.'
            : 'Envoi d’emails désactivé : pas d’invitation par email. Son lien d’inscription s’affichera dans la liste, à lui transmettre.'
        }
      >
        <div className="tw-grid tw-grid-cols-2 tw-gap-4 mq750:tw-grid-cols-1">
          <InputText
            value={user.last_name}
            setValue={update('last_name')}
            fieldTitle="Nom"
            isRequired
            emptyError={emptyError('Nom')}
            placeholder="Leblanc"
          />
          <InputText
            value={user.first_name}
            setValue={update('first_name')}
            fieldTitle="Prénom"
            isRequired
            emptyError={emptyError('Prénom')}
            placeholder="Juste"
          />
          <InputText
            value={user.email}
            setValue={update('email')}
            fieldTitle="E-mail"
            isRequired
            emptyError={emptyError('E-mail')}
            placeholder="exemple@exemple.com"
            isBadFormat={!isEmailValid(user.email)}
            type="email"
          />
          <InputText
            value={user.phone_number}
            setValue={update('phone_number')}
            fieldTitle="Téléphone"
            placeholder="0612345678"
            isBadFormat={isPhoneInvalid}
            type="tel"
            maxLength={10}
          />
        </div>
        {isEmailAlreadyListed && (
          <p className="tw-m-0 tw-text-[11px] tw-text-input-required">
            Cet email est déjà dans la liste.
          </p>
        )}
        <div className="tw-flex tw-justify-end">
          <MainButton
            text={addText}
            onClick={handleAdd}
            disabled={isDisabled}
            isLoading={isAdding}
          />
        </div>
      </FormSection>

      <GenericModal
        isOpen={userToRemove != null}
        setIsOpen={(isOpen) => !isOpen && setUserToRemove(null)}
        title="Supprimer l’administrateur"
        size={MODAL_SIZE_STYLE[MODAL_SIZE.MD]}
        secondaryActionText="Annuler"
        secondaryAction={() => setUserToRemove(null)}
        mainActionText="Supprimer"
        mainActionVariant="danger"
        mainAction={confirmRemove}
        mainActionIsLoading={isRemoving}
      >
        {userToRemove && (
          <div className="tw-flex tw-flex-col tw-gap-3">
            <p className="tw-m-0 tw-text-sm">
              {userToRemove.first_name} {userToRemove.last_name} (
              {userToRemove.email}) ne pourra plus se connecter au back-office
              du client. Son compte est archivé et la suppression est tracée
              dans l’historique.
            </p>
            {adminUsers.length === 1 && (
              <Banner tone="warn">
                C’est le dernier administrateur du client : plus personne ne
                pourra gérer ses utilisateurs et paramètres.
              </Banner>
            )}
          </div>
        )}
      </GenericModal>
    </Panel>
  );
}
